import {
    ACCOUNT_ID_KEY,
    forgetStoredValue,
    getStoredValue,
    HAS_ENTERED_HOME_KEY,
    SESSION_TOKEN_KEY,
} from '@sorrem-client/ui/shared/browser-storage';
import { forgetPostLoginPath } from './post-login-path';

type AuthSessionExpiredListener = () => void;

const authSessionExpiredListeners = new Set<AuthSessionExpiredListener>();

export function getStoredSessionToken(): string | null {
    return getStoredValue('session', SESSION_TOKEN_KEY) ?? null;
}

/** Stores a new login. Throws when storage is unavailable: a login that can't be kept must not look like it worked. */
export function persistAuthSession(accountId: string, token: string): void {
    window.sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    // Stored canonical, so a raw read and a canonical read name the same cache entries.
    window.sessionStorage.setItem(ACCOUNT_ID_KEY, toCanonicalAccountId(accountId));
    // A new login enters home afresh.
    window.sessionStorage.removeItem(HAS_ENTERED_HOME_KEY);
}

/**
 * Called when the server rejects the stored token (expired, revoked, or
 * signed before a server restart). Clears the local session and tells
 * subscribers - AppRoutes uses this to send the player back to login.
 */
export function expireAuthSession(): void {
    clearStoredSession();
    for (const listener of authSessionExpiredListeners) {
        listener();
    }
}

/** Subscribes to session expiry; returns the unsubscribe function. */
export function subscribeAuthSessionExpired(listener: AuthSessionExpiredListener): () => void {
    authSessionExpiredListeners.add(listener);
    return () => {
        authSessionExpiredListeners.delete(listener);
    };
}

/**
 * fetch() with the stored bearer token. A 401 for a request that carried a
 * token expires the session; a request sent without one (e.g. a dev-only
 * debug page with no login) just returns its 401.
 */
export async function fetchWithAuth(input: string, init: RequestInit = {}): Promise<Response> {
    const token = getStoredSessionToken();
    const headers = new Headers(init.headers);
    addBearerToken(headers, token);
    const response = await fetch(input, { ...init, headers });
    if (response.status === 401 && token !== null) {
        expireAuthSession();
    }

    return response;
}

/** Adds the session's bearer token to `headers`; without a token they are left alone. */
export function addBearerToken(headers: Headers, token: string | null = getStoredSessionToken()): void {
    if (token !== null) {
        headers.set('Authorization', `Bearer ${token}`);
    }
}

/** The stored account id as it was stored, or null when signed out. */
export function getStoredAccountId(): string | null {
    return getStoredValue('session', ACCOUNT_ID_KEY) ?? null;
}

/** The account id as RTK cache keys and API paths use it (trimmed, lowercase). */
export function toCanonicalAccountId(accountId: string): string {
    return accountId.trim().toLowerCase();
}

/** The stored account id in its canonical form, or null when signed out. */
export function getStoredCanonicalAccountId(): string | null {
    const accountId = getStoredAccountId();
    return accountId !== null ? toCanonicalAccountId(accountId) : null;
}

export function clearStoredSession(): void {
    forgetStoredValue('session', SESSION_TOKEN_KEY);
    forgetStoredValue('session', ACCOUNT_ID_KEY);
    forgetStoredValue('session', HAS_ENTERED_HOME_KEY);
}

/**
 * Best-effort server-side session revocation, then clears local session
 * state regardless of whether the network call succeeded - a client that
 * can't reach the server should still be able to log out locally.
 */
export async function logout(): Promise<void> {
    const token = getStoredSessionToken();
    if (token !== null) {
        const headers = new Headers();
        addBearerToken(headers, token);
        await fetch('/api/auth/session', { method: 'DELETE', headers }).catch(() => {
            // Best effort; local session cleanup below still proceeds.
        });
    }

    clearStoredSession();
    // An invite followed while signed out is for this login, not the next one.
    forgetPostLoginPath();
}
